import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { EmptyState } from "./EmptyState";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Plus,
  Send,
  Calendar,
  Target,
  Eye,
  MoreHorizontal,
  Search,
  Filter,
  TrendingUp,
  Play,
  Copy,
  Trash,
  Download,
  FileSpreadsheet,
  Users,
  XCircle,
  BarChart2,
} from "lucide-react";
import { apiListCampaigns, apiLaunchCampaign, apiDeleteCampaign, apiCloseCampaign, apiDuplicateCampaign, Campaign, TemplateSection, TemplateField } from "../lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { CampaignWizard } from "./CampaignWizard";
import { CampaignSupplierDialog } from "./CampaignSupplierDialog";
import { CampaignDashboard } from "./CampaignDashboard";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getStatusBadge(status: string) {
  switch (status) {
    case "ACTIVE":
      return <Badge className="bg-primary hover:bg-primary/90">Active</Badge>;
    case "DRAFT":
      return <Badge variant="outline">Brouillon</Badge>;
    case "CLOSED":
      return <Badge variant="secondary">Clôturée</Badge>;
    case "ARCHIVED":
      return <Badge variant="secondary">Archivée</Badge>;
    case "SCHEDULED":
      return <Badge className="bg-violet-500 hover:bg-violet-600">Planifiée</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

// ─── Skeleton loader ligne de table ──────────────────────────────────────────

function TableSkeleton() {
  return (
    <>
      {[1, 2, 3].map((i) => (
        <TableRow key={i}>
          <TableCell><div className="h-4 bg-muted rounded animate-pulse w-48" /></TableCell>
          <TableCell><div className="h-4 bg-muted rounded animate-pulse w-20" /></TableCell>
          <TableCell><div className="h-4 bg-muted rounded animate-pulse w-32" /></TableCell>
          <TableCell><div className="h-4 bg-muted rounded animate-pulse w-24" /></TableCell>
          <TableCell><div className="h-4 bg-muted rounded animate-pulse w-20" /></TableCell>
          <TableCell><div className="h-8 w-8 bg-muted rounded animate-pulse" /></TableCell>
        </TableRow>
      ))}
    </>
  );
}

// ─── Dialog prévisualisation formulaire ───────────────────────────────────────

type PreviewDialogProps = {
  campaign: Campaign
  open: boolean
  onClose: () => void
}

function FormPreviewDialog({ campaign, open, onClose }: PreviewDialogProps) {
  const sections = campaign.formTemplate?.sections as TemplateSection[] | undefined
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Aperçu — {campaign.formTemplate?.name}</DialogTitle>
        </DialogHeader>
        {Array.isArray(sections) && sections.length > 0 ? (
          <div className="space-y-4 mt-2">
            {sections.map((section) => (
              <div key={section.id} className="rounded-md border p-4 space-y-2">
                <h3 className="font-medium text-sm">{section.title}</h3>
                <ul className="space-y-1">
                  {section.fields.map((field: TemplateField) => (
                    <li key={field.id} className="text-sm flex items-center gap-2">
                      <span className="text-muted-foreground">{field.label}</span>
                      {field.required && (
                        <Badge variant="outline" className="text-xs py-0">Obligatoire</Badge>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground mt-2">Aucune section disponible.</p>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ─── Dialog détail campagne ───────────────────────────────────────────────────

type DetailDialogProps = {
  campaign: Campaign | null
  open: boolean
  onClose: () => void
}

function CampaignDetailDialog({ campaign, open, onClose }: DetailDialogProps) {
  const [showPreview, setShowPreview] = useState(false)
  if (!campaign) return null
  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{campaign.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Statut</span>
              {getStatusBadge(campaign.status)}
            </div>
            {campaign.description && (
              <div>
                <span className="text-muted-foreground">Description</span>
                <p className="mt-1 text-foreground">{campaign.description}</p>
              </div>
            )}
            {campaign.startsAt && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Début</span>
                <span>{new Date(campaign.startsAt).toLocaleDateString()}</span>
              </div>
            )}
            {campaign.dueAt && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Échéance</span>
                <span>{new Date(campaign.dueAt).toLocaleDateString()}</span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Template</span>
              {campaign.formTemplate ? (
                <div className="flex items-center gap-2">
                  <span>{campaign.formTemplate.name}</span>
                  <Button size="sm" variant="outline" onClick={() => setShowPreview(true)}>
                    <Eye className="h-3 w-3 mr-1" />
                    Aperçu du formulaire
                  </Button>
                </div>
              ) : (
                <span className="text-muted-foreground italic">Aucun template assigné</span>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {campaign.formTemplate && (
        <FormPreviewDialog
          campaign={campaign}
          open={showPreview}
          onClose={() => setShowPreview(false)}
        />
      )}
    </>
  )
}

// ─── Composant principal ─────────────────────────────────────────────────────

interface CampaignsProps {
  newCampaignOpen?: boolean;
  setNewCampaignOpen?: (open: boolean) => void;
  // Story 3.6 — deep-link depuis le profil fournisseur.
  // Quand non-null à l'arrivée, auto-ouvre le dashboard de la campagne
  // (et son détail fournisseur si campaignSupplierId fourni).
  initialTarget?: { campaignId: string; campaignSupplierId?: string } | null;
  // Callback consommé une fois après auto-ouverture pour qu'App reset
  // `pendingCampaignTarget` et que le target ne survive pas à une 2e
  // navigation via le menu.
  onTargetConsumed?: () => void;
  // Si fourni, le bouton « Retour » de SupplierDetailView (uniquement quand
  // on consulte le fournisseur du deep-link) revient au profil au lieu de
  // remonter à la liste dashboard.
  onBackToSupplierProfile?: () => void;
}

export function Campaigns({
  newCampaignOpen = false,
  setNewCampaignOpen,
  initialTarget = null,
  onTargetConsumed,
  onBackToSupplierProfile,
}: CampaignsProps) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showWizard, setShowWizard] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null)
  const [supplierCampaign, setSupplierCampaign] = useState<Campaign | null>(null)
  const [launchCampaign, setLaunchCampaign] = useState<Campaign | null>(null)
  const [isLaunching, setIsLaunching] = useState(false)
  const [deleteCampaign, setDeleteCampaign] = useState<Campaign | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [editCampaign, setEditCampaign] = useState<Campaign | null>(null)
  const [closeCampaign, setCloseCampaign] = useState<Campaign | null>(null)
  const [isClosing, setIsClosing] = useState(false)
  const [isDuplicating, setIsDuplicating] = useState(false)
  const [dashboardCampaign, setDashboardCampaign] = useState<Campaign | null>(null)
  // Story 3.6 — supplier deep-linké à propager vers `CampaignDashboard`
  // (auto-ouverture de SupplierDetailView). Stocké séparément de
  // `dashboardCampaign` car il survit à un rerender même quand `initialTarget`
  // est consommé/reset.
  const [initialSupplierId, setInitialSupplierId] = useState<string | null>(null)
  // Garde anti-double-consommation : useRef pour ne pas re-déclencher l'effet
  // d'auto-ouverture si initialTarget change de référence après consommation.
  const consumedTargetRef = useRef(false)

  // Synchroniser avec le prop externe (si le parent gère l'ouverture)
  useEffect(() => {
    if (newCampaignOpen) setShowWizard(true)
  }, [newCampaignOpen])

  useEffect(() => {
    apiListCampaigns()
      .then(setCampaigns)
      .catch(() => toast.error('Erreur lors du chargement des campagnes', { duration: Infinity }))
      .finally(() => setIsLoading(false))
  }, [])

  // Story 3.6 — Auto-ouverture du dashboard depuis le deep-link profil.
  // Dépend de [isLoading, campaigns, initialTarget] : on attend la fin du
  // chargement initial pour résoudre la campagne (sinon une liste vide due
  // au chargement async resterait bloquée sur la condition `length === 0`).
  // Si la campagne est introuvable (supprimée entre le clic et l'arrivée),
  // on signale l'erreur et on retombe sur la liste générique.
  useEffect(() => {
    if (!initialTarget || consumedTargetRef.current) return
    if (isLoading) return // attendre la fin du chargement initial
    const found = campaigns.find((c) => c.id === initialTarget.campaignId)
    consumedTargetRef.current = true
    if (!found) {
      toast.error('Campagne introuvable. Elle a peut-être été supprimée.', { duration: 6000 })
      onTargetConsumed?.()
      return
    }
    setDashboardCampaign(found)
    setInitialSupplierId(initialTarget.campaignSupplierId ?? null)
    onTargetConsumed?.()
  }, [isLoading, campaigns, initialTarget, onTargetConsumed])

  // P-1 — reset du garde quand le parent remet initialTarget à null (après
  // consommation via onTargetConsumed). Permet une 2e navigation vers la même
  // campagne sans que le composant soit démonté entre-temps.
  useEffect(() => {
    if (!initialTarget) consumedTargetRef.current = false
  }, [initialTarget])

  const handleOpenWizard = () => {
    setShowWizard(true)
    setNewCampaignOpen?.(true)
  }

  const handleCloseWizard = () => {
    setShowWizard(false)
    setNewCampaignOpen?.(false)
  }

  const handleCampaignSaved = (campaign: Campaign) => {
    setCampaigns((prev) => [campaign, ...prev])
    handleCloseWizard()
  }

  const handleLaunch = (campaign: Campaign) => {
    if (!campaign.formTemplateId) {
      toast.error('Vous devez sélectionner un template avant de lancer la campagne', { duration: Infinity })
      return
    }
    setLaunchCampaign(campaign)
  }

  const handleConfirmLaunch = async () => {
    if (!launchCampaign) return
    setIsLaunching(true)
    try {
      const result = await apiLaunchCampaign(launchCampaign.id)
      setCampaigns((prev) =>
        prev.map((c) => c.id === launchCampaign.id ? { ...c, status: 'ACTIVE' as const } : c)
      )
      toast.success(
        `Campagne lancée — ${result.suppliersNotified} invitation(s) envoyée(s)${result.emailFailures > 0 ? ` (${result.emailFailures} échec(s) email)` : ''}`,
        { duration: 4000 }
      )
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur lors du lancement'
      toast.error(msg, { duration: Infinity })
    } finally {
      setIsLaunching(false)
      setLaunchCampaign(null)
    }
  }

  const handleConfirmDelete = async () => {
    if (!deleteCampaign) return
    setIsDeleting(true)
    try {
      await apiDeleteCampaign(deleteCampaign.id)
      setCampaigns((prev) => prev.filter((c) => c.id !== deleteCampaign.id))
      toast.success('Campagne supprimée', { duration: 4000 })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur lors de la suppression'
      toast.error(msg, { duration: Infinity })
    } finally {
      setIsDeleting(false)
      setDeleteCampaign(null)
    }
  }

  const handleCampaignUpdated = (updated: Campaign) => {
    setCampaigns((prev) => prev.map((c) => c.id === updated.id ? updated : c))
    setEditCampaign(null)
  }

  const handleConfirmClose = async () => {
    if (!closeCampaign) return
    setIsClosing(true)
    try {
      const updated = await apiCloseCampaign(closeCampaign.id)
      setCampaigns(prev =>
        prev.map(c => c.id === closeCampaign.id ? { ...c, status: 'CLOSED' as const, closedAt: updated.closedAt } : c)
      )
      toast.success('Campagne clôturée avec succès', { duration: 4000 })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur lors de la clôture'
      toast.error(msg, { duration: Infinity })
    } finally {
      setIsClosing(false)
      setCloseCampaign(null)
    }
  }

  const handleDuplicate = async (campaign: Campaign) => {
    setIsDuplicating(true)
    try {
      const duplicate = await apiDuplicateCampaign(campaign.id)
      // Ajouter la copie en tête de liste (la plus récente en premier)
      setCampaigns(prev => [
        { ...duplicate, supplierCount: 0, submittedCount: 0, inProgressCount: 0, notStartedCount: 0 },
        ...prev,
      ])
      toast.success(`Campagne dupliquée : "${duplicate.name}"`, { duration: 4000 })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur lors de la duplication'
      toast.error(msg, { duration: Infinity })
    } finally {
      setIsDuplicating(false)
    }
  }

  // Métriques calculées depuis le state réel
  const activeCampaigns = campaigns.filter((c) => c.status === 'ACTIVE')
  const draftCampaigns = campaigns.filter((c) => c.status === 'DRAFT')

  // Filtrage
  const filteredCampaigns = campaigns.filter((campaign) => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === "all" || campaign.status === statusFilter
    return matchesSearch && matchesStatus
  })

  if (dashboardCampaign) {
    return (
      <CampaignDashboard
        campaignId={dashboardCampaign.id}
        campaignName={dashboardCampaign.name}
        initialSupplierId={initialSupplierId}
        onBack={() => {
          setDashboardCampaign(null)
          setInitialSupplierId(null)
        }}
        onBackToSupplierProfile={onBackToSupplierProfile}
      />
    )
  }

  return (
    <div className="space-y-6">
      {showWizard && (
        <CampaignWizard
          onSave={handleCampaignSaved}
          onCancel={handleCloseWizard}
        />
      )}

      {selectedCampaign && (
        <CampaignDetailDialog
          campaign={selectedCampaign}
          open={!!selectedCampaign}
          onClose={() => setSelectedCampaign(null)}
        />
      )}

      {supplierCampaign && (
        <CampaignSupplierDialog
          campaignId={supplierCampaign.id}
          campaignName={supplierCampaign.name}
          campaignStatus={supplierCampaign.status}
          open={!!supplierCampaign}
          onClose={() => setSupplierCampaign(null)}
        />
      )}

      {editCampaign && (
        <CampaignWizard
          initialCampaign={editCampaign}
          onSave={handleCampaignUpdated}
          onCancel={() => setEditCampaign(null)}
        />
      )}

      <AlertDialog open={!!deleteCampaign} onOpenChange={(open) => { if (!open) setDeleteCampaign(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la campagne</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer <strong>"{deleteCampaign?.name}"</strong> ?
              Cette action est irréversible. Les fournisseurs assignés seront également supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!launchCampaign} onOpenChange={(open) => { if (!open) setLaunchCampaign(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Lancer la campagne</AlertDialogTitle>
            <AlertDialogDescription>
              Voulez-vous lancer la campagne <strong>"{launchCampaign?.name}"</strong> ?
              Les invitations email seront envoyées automatiquement à tous les fournisseurs assignés.
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLaunching}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmLaunch} disabled={isLaunching}>
              {isLaunching ? 'Lancement en cours...' : 'Lancer la campagne'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!closeCampaign} onOpenChange={(open) => { if (!open) setCloseCampaign(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clôturer la campagne</AlertDialogTitle>
            <AlertDialogDescription>
              Voulez-vous clôturer la campagne <strong>"{closeCampaign?.name}"</strong> ?
              Cette action est irréversible. Les fournisseurs ne pourront plus soumettre de données.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isClosing}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmClose}
              disabled={isClosing}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isClosing ? 'Clôture en cours...' : 'Clôturer la campagne'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1>Gestion des campagnes</h1>
          <p className="text-muted-foreground">
            Créez et gérez vos campagnes de collecte de données fournisseurs
          </p>
        </div>
        <Button onClick={handleOpenWizard}>
          <Plus className="mr-2 h-4 w-4" />
          Nouvelle campagne
        </Button>
      </div>

      {/* Métriques */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle>Campagnes actives</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeCampaigns.length}</div>
            <p className="text-xs text-muted-foreground">
              {draftCampaigns.length} en brouillon
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle>Total campagnes</CardTitle>
            <Send className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{campaigns.length}</div>
            <p className="text-xs text-muted-foreground">Toutes statuts confondus</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle>Avec template</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {campaigns.filter((c) => !!c.formTemplateId).length}
            </div>
            <p className="text-xs text-muted-foreground">Template assigné</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle>Clôturées</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {campaigns.filter((c) => c.status === 'CLOSED').length}
            </div>
            <p className="text-xs text-muted-foreground">Campagnes terminées</p>
          </CardContent>
        </Card>
      </div>

      {/* Tableau des campagnes */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Toutes les campagnes</CardTitle>
              <CardDescription>
                Suivez et gérez vos campagnes de durabilité
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filtres */}
          <div className="flex items-center space-x-4 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Rechercher une campagne..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="DRAFT">Brouillon</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="SCHEDULED">Planifiée</SelectItem>
                <SelectItem value="CLOSED">Clôturée</SelectItem>
                <SelectItem value="ARCHIVED">Archivée</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campagne</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Template</TableHead>
                  <TableHead>Échéance</TableHead>
                  <TableHead>Progression</TableHead>
                  <TableHead className="w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableSkeleton />
                ) : filteredCampaigns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-0">
                      <EmptyState
                        icon="🎯"
                        title="Pas encore de campagne"
                        description="Créez votre première campagne pour commencer à collecter les données de vos fournisseurs."
                        primaryLabel="Créer via l'agent"
                        secondaryLabel="+ Nouvelle campagne"
                        onSecondaryClick={handleOpenWizard}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCampaigns.map((campaign) => (
                    <TableRow key={campaign.id}>
                      <TableCell>
                        <div>
                          <button
                            className="font-medium hover:underline text-left"
                            onClick={() => setSelectedCampaign(campaign)}
                          >
                            {campaign.name}
                          </button>
                          {campaign.category && (
                            <p className="text-xs text-muted-foreground">{campaign.category}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {campaign.formTemplate?.name ?? '—'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {campaign.dueAt ? (
                          <span className="text-sm">
                            {new Date(campaign.dueAt).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm space-y-1">
                          <div className="font-medium">{campaign.supplierCount ?? 0} fournisseur{(campaign.supplierCount ?? 0) > 1 ? 's' : ''}</div>
                          <div className="text-muted-foreground flex gap-3 text-xs">
                            <span className="text-primary">{campaign.submittedCount ?? 0} soumis</span>
                            <span className="text-amber-500">{campaign.inProgressCount ?? 0} en cours</span>
                            <span className="text-muted-foreground">{campaign.notStartedCount ?? 0} non démarrés</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <span className="sr-only">Ouvrir le menu</span>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => setSelectedCampaign(campaign)}>
                              <Eye className="mr-2 h-4 w-4" />
                              Voir les détails
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setSupplierCampaign(campaign)}>
                              <Users className="mr-2 h-4 w-4" />
                              Gérer les fournisseurs
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDuplicate(campaign)}
                              disabled={isDuplicating}
                            >
                              <Copy className="mr-2 h-4 w-4" />
                              {isDuplicating ? 'Duplication...' : 'Dupliquer'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {campaign.status === 'DRAFT' && (
                              <>
                                <DropdownMenuItem onClick={() => setEditCampaign(campaign)}>
                                  <Copy className="mr-2 h-4 w-4" />
                                  Modifier
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleLaunch(campaign)}>
                                  <Play className="mr-2 h-4 w-4" />
                                  Lancer
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => setDeleteCampaign(campaign)}
                                >
                                  <Trash className="mr-2 h-4 w-4" />
                                  Supprimer
                                </DropdownMenuItem>
                              </>
                            )}
                            {(campaign.status === 'ACTIVE' || campaign.status === 'CLOSED') && (
                              <DropdownMenuItem onClick={() => setDashboardCampaign(campaign)}>
                                <BarChart2 className="mr-2 h-4 w-4" />
                                Tableau de bord
                              </DropdownMenuItem>
                            )}
                            {campaign.status === 'ACTIVE' && (
                              <DropdownMenuItem onClick={() => setCloseCampaign(campaign)}>
                                <XCircle className="mr-2 h-4 w-4" />
                                Clôturer
                              </DropdownMenuItem>
                            )}
                            {campaign.status !== 'DRAFT' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem disabled>
                                  <Download className="mr-2 h-4 w-4" />
                                  Télécharger les résultats
                                </DropdownMenuItem>
                                <DropdownMenuItem disabled>
                                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                                  Exporter Excel
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
